import { PushNotificationGroup, PushNotificationGroupState, PushNotificationSetting } from '@connect-x-app/shared-domain-models';

const BASE_KEYS_IN_ORDER = ['SMART_ACTIONS', 'MENTIONS', 'USERS', 'SYSTEM_MESSAGES'] as const;
export type BasePushNotificationKey = (typeof BASE_KEYS_IN_ORDER)[number];

const BASE_KEY_BY_KEY: Record<PushNotificationSetting['key'], BasePushNotificationKey> = {
  CUSTOM_CHANNEL_MENTIONS: 'MENTIONS',
  CUSTOM_CHANNEL_SMART_ACTIONS: 'SMART_ACTIONS',
  CUSTOM_CHANNEL_SYSTEM_MESSAGES: 'SYSTEM_MESSAGES',
  CUSTOM_CHANNEL_USERS: 'USERS',
  MENTIONS: 'MENTIONS',
  SMART_ACTIONS: 'SMART_ACTIONS',
  SYSTEM_MESSAGES: 'SYSTEM_MESSAGES',
  USERS: 'USERS',
};

const CUSTOM_CHANNEL_KEY_BY_BASE_KEY: Record<BasePushNotificationKey, PushNotificationSetting['key']> = {
  MENTIONS: 'CUSTOM_CHANNEL_MENTIONS',
  SMART_ACTIONS: 'CUSTOM_CHANNEL_SMART_ACTIONS',
  SYSTEM_MESSAGES: 'CUSTOM_CHANNEL_SYSTEM_MESSAGES',
  USERS: 'CUSTOM_CHANNEL_USERS',
};

export const getBasePushNotificationKey = (key: PushNotificationSetting['key']): BasePushNotificationKey => BASE_KEY_BY_KEY[key];

export const getPushNotificationKeyForGroup = (
  baseKey: BasePushNotificationKey,
  group: PushNotificationGroup,
): PushNotificationSetting['key'] => (group === 'CUSTOM_CHANNELS' ? CUSTOM_CHANNEL_KEY_BY_BASE_KEY[baseKey] : baseKey);

export const getPushNotificationGroupKeys = (group: PushNotificationGroup): PushNotificationSetting['key'][] =>
  BASE_KEYS_IN_ORDER.map(baseKey => getPushNotificationKeyForGroup(baseKey, group));

const getPushNotificationGroup = (key: PushNotificationSetting['key']): PushNotificationGroup =>
  key === CUSTOM_CHANNEL_KEY_BY_BASE_KEY[getBasePushNotificationKey(key)] ? 'CUSTOM_CHANNELS' : 'MAIN_ROOM';

export const sortPushNotificationSettings = (settings: PushNotificationSetting[]): PushNotificationSetting[] =>
  [...settings].sort(
    (a, b) =>
      BASE_KEYS_IN_ORDER.indexOf(getBasePushNotificationKey(a.key)) -
      BASE_KEYS_IN_ORDER.indexOf(getBasePushNotificationKey(b.key)),
  );

export const getPushNotificationSettingsForGroup = (
  settings: PushNotificationSetting[],
  group: PushNotificationGroup,
): PushNotificationSetting[] => {
  const groupKeys = getPushNotificationGroupKeys(group);

  return sortPushNotificationSettings(settings.filter(({ key }) => groupKeys.includes(key)));
};

export const getPushNotificationGroupState = (
  settings: PushNotificationSetting[],
  group: PushNotificationGroup,
): PushNotificationGroupState => {
  const groupSettings = getPushNotificationSettingsForGroup(settings, group);

  if (groupSettings.every(({ isEnabled }) => isEnabled)) {
    return 'ON';
  }

  return groupSettings.every(({ isEnabled }) => !isEnabled) ? 'OFF' : 'CUSTOM';
};

export const getPushNotificationKeysToUpdate = (
  key: PushNotificationSetting['key'],
  isEnabled: boolean,
): PushNotificationSetting['key'][] => {
  const isDisablingMentions = getBasePushNotificationKey(key) === 'MENTIONS' && !isEnabled;

  return isDisablingMentions ? [key, getPushNotificationKeyForGroup('USERS', getPushNotificationGroup(key))] : [key];
};
